// Comprehensive error handling utilities
import { 
  AppError, 
  ErrorCategory, 
  ErrorSeverity, 
  NetworkError, 
  AuthenticationError, 
  ValidationError, 
  ServiceUnavailableError,
  ErrorContext,
  ErrorRecoveryStrategy 
} from '../types/errors';

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorReportingEnabled: boolean = true;
  private retryAttempts: Map<string, number> = new Map();
  private maxRetryAttempts: number = 3;

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Convert various error types to standardized AppError
   */
  normalizeError(error: any, context?: Partial<ErrorContext>): AppError {
    const timestamp = new Date().toISOString();
    const baseContext = {
      timestamp,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context,
    };

    // Handle network errors
    if (error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') {
      return this.createNetworkError(error, baseContext);
    }

    // Handle AWS Cognito authentication errors
    if (error.code && this.isAuthenticationError(error.code)) {
      return this.createAuthenticationError(error, baseContext);
    }

    // Handle validation errors
    if (error.name === 'ValidationError' || error.field) {
      return this.createValidationError(error, baseContext);
    }

    // Handle service unavailable errors
    if (error.code === 'SERVICE_UNAVAILABLE' || error.statusCode >= 500) {
      return this.createServiceUnavailableError(error, baseContext);
    }

    // Handle rate limiting
    if (error.code === 'TooManyRequestsException' || error.statusCode === 429) {
      return this.createRateLimitError(error, baseContext);
    }

    // Default unknown error
    return this.createUnknownError(error, baseContext);
  }

  /**
   * Create user-friendly network error
   */
  private createNetworkError(error: any, context: ErrorContext): NetworkError {
    let userMessage = "I'm having trouble connecting to the internet. Please check your connection and try again.";
    let suggestions = [
      "Check your internet connection",
      "Try refreshing the page",
      "Wait a moment and try again"
    ];

    if (error.statusCode === 0 || !navigator.onLine) {
      userMessage = "It looks like you're not connected to the internet. Please check your connection.";
      suggestions = [
        "Check your Wi-Fi or internet connection",
        "Make sure you're connected to the internet",
        "Try opening another website to test your connection"
      ];
    } else if (error.statusCode >= 500) {
      userMessage = "Our servers are having a temporary problem. Please try again in a few minutes.";
      suggestions = [
        "Wait a few minutes and try again",
        "The problem is on our end, not yours",
        "Try refreshing the page"
      ];
    }

    return {
      code: error.code || 'NETWORK_ERROR',
      message: error.message || 'Network connection failed',
      userMessage,
      category: ErrorCategory.NETWORK,
      severity: ErrorSeverity.MEDIUM,
      timestamp: context.timestamp,
      context,
      retryable: true,
      suggestions,
      statusCode: error.statusCode,
      endpoint: error.config?.url,
    };
  }

  /**
   * Create user-friendly authentication error
   */
  private createAuthenticationError(error: any, context: ErrorContext): AuthenticationError {
    const errorMappings: Record<string, { message: string; suggestions: string[] }> = {
      'NotAuthorizedException': {
        message: "Your email or password is incorrect. Please check and try again.",
        suggestions: [
          "Double-check your email address",
          "Make sure your password is correct",
          "Use 'Forgot Password' if you can't remember"
        ]
      },
      'UserNotFoundException': {
        message: "I can't find an account with that email address. Please check your email or create a new account.",
        suggestions: [
          "Check if you typed your email correctly",
          "Try creating a new account if you haven't signed up yet",
          "Make sure you're using the right email address"
        ]
      },
      'UserNotConfirmedException': {
        message: "Please check your email and click the verification link before signing in.",
        suggestions: [
          "Check your email inbox for a verification message",
          "Look in your spam folder too",
          "Click the verification link in the email"
        ]
      },
      'PasswordResetRequiredException': {
        message: "You need to reset your password. Please use the 'Forgot Password' option.",
        suggestions: [
          "Click 'Forgot Password' below",
          "Check your email for reset instructions",
          "Create a new password when prompted"
        ]
      },
      'TooManyRequestsException': {
        message: "Too many sign-in attempts. Please wait a few minutes and try again.",
        suggestions: [
          "Wait 5-10 minutes before trying again",
          "Make sure you're using the correct password",
          "Try resetting your password if you're unsure"
        ]
      }
    };

    const mapping = errorMappings[error.code] || {
      message: "There was a problem signing you in. Please try again.",
      suggestions: ["Try again in a moment", "Check your internet connection"]
    };

    return {
      code: error.code || 'AUTH_ERROR',
      message: error.message || 'Authentication failed',
      userMessage: mapping.message,
      category: ErrorCategory.AUTHENTICATION,
      severity: ErrorSeverity.MEDIUM,
      timestamp: context.timestamp,
      context,
      retryable: false,
      suggestions: mapping.suggestions,
      authCode: error.code,
    };
  }

  /**
   * Create user-friendly validation error
   */
  private createValidationError(error: any, context: ErrorContext): ValidationError {
    let userMessage = "Please check the information you entered and try again.";
    let suggestions = ["Review the form for any errors", "Make sure all required fields are filled"];

    if (error.field === 'email') {
      userMessage = "Please enter a valid email address.";
      suggestions = ["Make sure your email has an @ symbol", "Check for typos in your email"];
    } else if (error.field === 'password') {
      userMessage = "Please check your password requirements.";
      suggestions = ["Password must be at least 8 characters", "Include both letters and numbers"];
    }

    return {
      code: error.code || 'VALIDATION_ERROR',
      message: error.message || 'Validation failed',
      userMessage,
      category: ErrorCategory.VALIDATION,
      severity: ErrorSeverity.LOW,
      timestamp: context.timestamp,
      context,
      retryable: false,
      suggestions,
      field: error.field,
      validationRule: error.rule,
    };
  }

  /**
   * Create service unavailable error
   */
  private createServiceUnavailableError(error: any, context: ErrorContext): ServiceUnavailableError {
    return {
      code: error.code || 'SERVICE_UNAVAILABLE',
      message: error.message || 'Service temporarily unavailable',
      userMessage: "I'm having trouble right now, but I should be back up soon. Please try again in a few minutes.",
      category: ErrorCategory.SERVICE_UNAVAILABLE,
      severity: ErrorSeverity.HIGH,
      timestamp: context.timestamp,
      context,
      retryable: true,
      suggestions: [
        "Wait a few minutes and try again",
        "The problem is temporary",
        "Try refreshing the page"
      ],
      serviceName: error.service || 'Unknown Service',
      fallbackAvailable: false,
    };
  }

  /**
   * Create rate limit error
   */
  private createRateLimitError(error: any, context: ErrorContext): AppError {
    return {
      code: 'RATE_LIMIT_EXCEEDED',
      message: 'Rate limit exceeded',
      userMessage: "You're going a bit too fast! Please wait a moment before trying again.",
      category: ErrorCategory.RATE_LIMIT,
      severity: ErrorSeverity.MEDIUM,
      timestamp: context.timestamp,
      context,
      retryable: true,
      suggestions: [
        "Wait a minute before trying again",
        "Take your time - there's no rush",
        "Try again in a few moments"
      ],
    };
  }

  /**
   * Create unknown error
   */
  private createUnknownError(error: any, context: ErrorContext): AppError {
    return {
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
      userMessage: "Something unexpected happened. Don't worry - let's try that again.",
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.MEDIUM,
      timestamp: context.timestamp,
      context,
      retryable: true,
      suggestions: [
        "Try the action again",
        "Refresh the page if the problem continues",
        "Contact support if this keeps happening"
      ],
    };
  }

  /**
   * Check if error code is authentication related
   */
  private isAuthenticationError(code: string): boolean {
    const authCodes = [
      'NotAuthorizedException',
      'UserNotFoundException',
      'UserNotConfirmedException',
      'PasswordResetRequiredException',
      'TooManyRequestsException',
      'InvalidParameterException',
      'CodeMismatchException',
      'ExpiredCodeException',
    ];
    return authCodes.includes(code);
  }

  /**
   * Get recovery strategies for an error
   */
  getRecoveryStrategies(error: AppError): ErrorRecoveryStrategy[] {
    const strategies: ErrorRecoveryStrategy[] = [];

    // Add retry strategy for retryable errors
    if (error.retryable) {
      const retryCount = this.retryAttempts.get(error.code) || 0;
      if (retryCount < this.maxRetryAttempts) {
        strategies.push({
          type: 'retry',
          label: 'Try Again',
          description: 'Attempt the action again',
          action: () => this.incrementRetryCount(error.code),
        });
      }
    }

    // Add specific strategies based on error category
    switch (error.category) {
      case ErrorCategory.NETWORK:
        strategies.push({
          type: 'manual',
          label: 'Check Connection',
          description: 'Verify your internet connection',
        });
        break;

      case ErrorCategory.AUTHENTICATION:
        strategies.push({
          type: 'redirect',
          label: 'Sign In Again',
          description: 'Go to the sign-in page',
        });
        break;

      case ErrorCategory.SERVICE_UNAVAILABLE:
        strategies.push({
          type: 'fallback',
          label: 'Use Offline Mode',
          description: 'Continue with limited functionality',
        });
        break;
    }

    return strategies;
  }

  /**
   * Increment retry count for error tracking
   */
  private incrementRetryCount(errorCode: string): void {
    const current = this.retryAttempts.get(errorCode) || 0;
    this.retryAttempts.set(errorCode, current + 1);
  }

  /**
   * Reset retry count for an error
   */
  resetRetryCount(errorCode: string): void {
    this.retryAttempts.delete(errorCode);
  }

  /**
   * Check if network is available
   */
  async checkNetworkConnectivity(): Promise<boolean> {
    if (!navigator.onLine) {
      return false;
    }

    try {
      // Try to fetch a small resource to test connectivity
      const response = await fetch('/favicon.ico', { 
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Report error for analytics and debugging
   */
  async reportError(error: AppError, userFeedback?: string): Promise<void> {
    if (!this.errorReportingEnabled) {
      return;
    }

    try {
      // In a real implementation, this would send to an error reporting service
      console.error('Error Report:', {
        error,
        userFeedback,
        timestamp: new Date().toISOString(),
      });

      // Could integrate with services like Sentry, LogRocket, etc.
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  }

  /**
   * Enable or disable error reporting
   */
  setErrorReporting(enabled: boolean): void {
    this.errorReportingEnabled = enabled;
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Utility functions
export const normalizeError = (error: any, context?: Partial<ErrorContext>): AppError => {
  return errorHandler.normalizeError(error, context);
};

export const getRecoveryStrategies = (error: AppError): ErrorRecoveryStrategy[] => {
  return errorHandler.getRecoveryStrategies(error);
};

export const checkNetworkConnectivity = (): Promise<boolean> => {
  return errorHandler.checkNetworkConnectivity();
};